// =====================================================================
// src/pages/intl-acca/QualifierIntelPage.tsx
// =====================================================================
// Elite-tier surface (PRD §3 / §10.2 — deferred to v1.1).
// Pre-tournament signals across CAF / UEFA / CONMEBOL / etc. windows.
//
// Routes mounted at: /intl-acca/qualifier/:windowId
//
// LOCKED BY DEFAULT for non-Elite viewers — host wraps the whole
// surface in <TierLockedTeaser gate="qualifier_intel">.

import type {
  QualifierWindow,
  QualifierSignal,
  QualifierWatchlistItem,
} from "../../db/intl-acca-types-extended";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { TierLockedTeaser } from "../../components/intl-acca/TierLockedTeaser";

export interface QualifierIntelPageProps {
  loading: boolean;
  error: string | null;
  window: QualifierWindow | null;
  signals: QualifierSignal[];
  watchlist: QualifierWatchlistItem[];
  hasEliteAccess: boolean;
  onUpgrade?: () => void;
}

export function QualifierIntelPage(props: QualifierIntelPageProps): JSX.Element {
  const { loading, error, window, signals, watchlist, hasEliteAccess, onUpgrade } = props;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading qualifier window…</div>
      </div>
    );
  }
  if (error || !window) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="No qualifier window data."
          body={error ?? "No window matched. Qualifier signals migration is a v1.1 deliverable."}
        />
      </div>
    );
  }

  const content = (
    <>
      <header className="ia-page-header">
        <div>
          <div className="ia-eyebrow">Elite-tier intelligence · {window.name}</div>
          <h1>
            Qualifier Intel. <em>Pre-tournament edge.</em>
          </h1>
          <p className="ia-sub">
            The qualifier window is where DNA profiles form and pricing models
            calibrate. Elite tier sees the working signals before they reach
            the wider tournament product.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexShrink: 0 }}>
          <span className="ia-pill ia-pill-amber">{window.signals_active} ACTIVE</span>
          <span className="ia-pill ia-pill-info">{window.signals_speculative} WATCH</span>
          <span className="ia-pill ia-pill-ghost">{window.matches_total} MATCHES</span>
        </div>
      </header>

      <div className="ia-page-pad">
        {/* Top signals */}
        <div className="ia-eyebrow" style={{ marginBottom: 16 }}>
          Top signals · this window
        </div>
        {signals.length === 0 ? (
          <HonestEmptyState
            variant="custom"
            title="No active signals."
            body="The engine publishes signals only when confidence + adversarial review clear. Empty by design when nothing meets the bar."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {signals.map((s) => (
              <QualifierRow key={s.id} signal={s} />
            ))}
          </div>
        )}

        {/* Watchlist */}
        <div style={{ marginTop: 40 }}>
          <div className="ia-eyebrow" style={{ marginBottom: 14 }}>
            Watchlist · monitoring · not active
          </div>
          {watchlist.length === 0 ? (
            <p
              className="ia-mono"
              style={{
                fontSize: "var(--sz-mono-sm)",
                color: "var(--text-tertiary)",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              No watch items
            </p>
          ) : (
            <div className="ia-card" style={{ padding: 0 }}>
              {watchlist.map((w, i) => (
                <div
                  key={i}
                  style={{
                    padding: "var(--sz-space-4) var(--sz-space-5)",
                    display: "grid",
                    gridTemplateColumns: "1fr 130px 1fr",
                    alignItems: "center",
                    gap: 18,
                    borderTop: i > 0 ? "1px solid var(--border-faint)" : "none",
                  }}
                >
                  <span style={{ fontSize: "var(--sz-body-sm)", color: "var(--text)" }}>
                    {w.match_label}
                  </span>
                  <span
                    className={
                      w.flag === "ACTIVE"
                        ? "ia-pill ia-pill-amber"
                        : w.flag === "SPECULATIVE"
                          ? "ia-pill ia-pill-info"
                          : "ia-pill ia-pill-ghost"
                    }
                  >
                    {w.flag}
                  </span>
                  <span
                    style={{ fontSize: "var(--sz-body-sm)", color: "var(--text-secondary)" }}
                  >
                    {w.note}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Why-it-matters footer */}
        <div
          style={{
            marginTop: 32,
            padding: 20,
            background: "var(--bg-elev)",
            border: "1px solid var(--sz-amber-dim)",
            borderRadius: "var(--sz-radius-lg)",
            display: "flex",
            gap: 16,
            alignItems: "center",
          }}
        >
          <span
            className="ia-mono"
            style={{ fontSize: 18, color: "var(--accent)" }}
          >
            ★
          </span>
          <div
            style={{
              fontSize: "var(--sz-body-sm)",
              color: "var(--text-secondary)",
              lineHeight: 1.6,
              flex: 1,
            }}
          >
            <strong style={{ color: "var(--text)" }}>Why qualifier intel matters.</strong>{" "}
            Every signal here generates a CLV data point that feeds the next
            tournament window's model. Manager DNA profiles update mid-window;
            squad freshness baselines are established for the coming
            tournament window.
          </div>
        </div>
      </div>
    </>
  );

  if (!hasEliteAccess) {
    return (
      <div className="ia-root">
        <div className="ia-page-pad">
          <TierLockedTeaser gate="qualifier_intel" onUpgrade={onUpgrade}>
            {content}
          </TierLockedTeaser>
        </div>
      </div>
    );
  }

  return <div className="ia-root">{content}</div>;
}

function QualifierRow({ signal }: { signal: QualifierSignal }): JSX.Element {
  const conf = signal.clv_confidence;
  const cls =
    conf === "HIGH" ? "high" :
    conf === "MEDIUM" ? "medium" : "speculative";

  return (
    <div className={`ia-qualifier-row ${cls}`}>
      <div>
        <div
          className="ia-mono"
          style={{
            fontSize: "var(--sz-mono-xs)",
            color: "var(--text-tertiary)",
            letterSpacing: "0.18em",
          }}
        >
          WCQ · {signal.match_label.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--sz-font-display)",
            fontSize: 24,
            letterSpacing: "0.04em",
            marginTop: 6,
            color: "var(--text)",
          }}
        >
          {signal.match_label}
        </div>
        <div
          style={{
            fontFamily: "var(--sz-font-head)",
            fontSize: 16,
            marginTop: 6,
            color: "var(--text)",
          }}
        >
          <span style={{ color: "var(--accent)" }}>{signal.selection}</span>
          <span
            className="ia-mono"
            style={{
              color: "var(--text-tertiary)",
              fontSize: 13,
              marginLeft: 8,
            }}
          >
            · STRIKZA {signal.strikza_price.toFixed(2)} / mkt {signal.market_price.toFixed(2)}
          </span>
        </div>
        {/* Reasoning never hidden (PRD §12) */}
        <p
          style={{
            marginTop: 10,
            fontSize: "var(--sz-body-sm)",
            color: "var(--text-secondary)",
            lineHeight: 1.55,
            maxWidth: 720,
          }}
        >
          {signal.reasoning_note}
        </p>
      </div>

      {/* Edge bar / honest empty */}
      <div>
        <div className="ia-metric-label">EDGE</div>
        {signal.edge_pp == null ? (
          <div className="ia-metric-value-empty" style={{ marginTop: 8 }}>
            Awaiting clearance
          </div>
        ) : (
          <div className="ia-bar-mini" style={{ marginTop: 8 }}>
            <div
              className="ia-bar-mini-fill"
              style={{ width: `${Math.min(100, Math.abs(signal.edge_pp) * 10)}%` }}
            />
          </div>
        )}
        {signal.edge_pp != null ? (
          <div
            className="ia-mono"
            style={{
              marginTop: 6,
              fontSize: "var(--sz-mono-sm)",
              color: signal.edge_pp > 0 ? "var(--accent-text)" : "var(--neg)",
            }}
          >
            {signal.edge_pp > 0 ? "+" : ""}{signal.edge_pp.toFixed(1)}pp
          </div>
        ) : null}
      </div>

      <div style={{ textAlign: "right" }}>
        <div className="ia-metric-label">CONFIDENCE</div>
        <div
          style={{
            fontFamily: "var(--sz-font-display)",
            fontSize: 22,
            marginTop: 6,
            lineHeight: 1,
            color:
              conf === "HIGH" ? "var(--pos)" :
              conf === "MEDIUM" ? "var(--accent)" : "var(--info)",
          }}
        >
          {conf}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// // Future tables (deferred per PRD §10.2):
// //   intl_acca_qualifier_windows
// //   intl_acca_qualifier_signals
// const { data: window }    = await sb.from('intl_acca_qualifier_windows')
//                                    .select('*').eq('id', windowId).single();
// const { data: signals }   = await sb.from('intl_acca_qualifier_signals')
//                                    .select('*').eq('window_id', windowId)
//                                    .eq('flag', 'ACTIVE').order('edge_pp', { ascending: false });
// const { data: watchlist } = await sb.from('intl_acca_qualifier_signals')
//                                    .select('match_label, flag, reasoning_note as note')
//                                    .eq('window_id', windowId)
//                                    .in('flag', ['SPECULATIVE', 'NEUTRAL']);
//
// const hasEliteAccess = tierMeets(currentTier, 'desk_pro');
